/**
 * Clone Template Dialog Component
 * Dialog to clone a template with new name and description
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useGlobalConfig } from 'context/GlobalContext';
import { BglPrimarySmallButton, BglSecondarySmallButton } from 'components/button/BglButtons';
import { ConfigRegistryApis } from '../api/configRegistryAPI';
import { Template } from '../types/configRegistryTypes';

interface CloneTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  template: Template;
  onCloned: () => void;
}

export const CloneTemplateDialog = ({
  open,
  onClose,
  template,
  onCloned,
}: CloneTemplateDialogProps) => {
  const { rc } = useGlobalConfig();
  
  const [name, setName] = useState(`${template.name} (Copy)`);
  const [description, setDescription] = useState(template.description || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setName(`${template.name} (Copy)`);
    setDescription(template.description || '');
    setError(null);
    onClose();
  };

  const handleClone = async () => {
    if (!name.trim()) {
      setError('Template name is required');
      return;
    }
    
    setIsSaving(true);
    setError(null);
    try {
      const response = await ConfigRegistryApis.cloneTemplate(
        rc,
        template.documentId,
        name.trim(),
        description.trim() || undefined
      );
      
      if (response.error) {
        setError(response.error.message || 'Failed to clone template');
      } else {
        onCloned();
        handleClose();
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while cloning');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ContentCopyIcon color="primary" />
        Clone Template
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Create a copy of "{template.name}" with all its linked configs and values.
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="New Template Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            error={!name.trim()}
            helperText={!name.trim() ? 'Name is required' : ''}
          />
          
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder="Optional description for the cloned template"
          />
        </Box>
        
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Source Template:</strong> {template.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Configs to copy:</strong> {template.items?.length || 0}
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <BglSecondarySmallButton onClick={handleClose} disabled={isSaving}>
          Cancel
        </BglSecondarySmallButton>
        <BglPrimarySmallButton
          onClick={handleClone}
          disabled={!name.trim() || isSaving}
          startIcon={isSaving ? <CircularProgress size={16} /> : <ContentCopyIcon />}
        >
          {isSaving ? 'Cloning...' : 'Clone Template'}
        </BglPrimarySmallButton>
      </DialogActions>
    </Dialog>
  );
};

export default CloneTemplateDialog;

